/**
 * Custom terminal component for GuruConnect remote shell.
 *
 * No third-party terminal libraries -- pure React + TypeScript.
 * Renders a scrolling monospace output buffer with ANSI color parsing,
 * a text input line with command history, and supports terminal resize.
 */

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { parseAnsi, type StyledSpan } from "./ansi";
import "./terminal.css";

/** Maximum lines kept in the scrollback buffer */
const MAX_SCROLLBACK = 5000;

export interface TerminalHandle {
  appendOutput: (data: string) => void;
  clear: () => void;
}

export interface TerminalProps {
  /** Called when the user types input (keystrokes sent to the shell) */
  onInput: (data: string) => void;
  /** Called when the terminal container resizes (cols, rows) */
  onResize?: (cols: number, rows: number) => void;
  /** Whether the shell is connected and accepting input */
  connected: boolean;
  /** Optional status text shown in the footer bar */
  status?: string;
}

interface TermLine {
  spans: StyledSpan[];
}

function measureCells(container: HTMLElement): { cols: number; rows: number } {
  const measure = document.createElement("span");
  measure.style.cssText =
    "position:absolute;visibility:hidden;white-space:pre;font:inherit";
  measure.textContent = "M";
  container.appendChild(measure);
  const charW = measure.offsetWidth;
  const charH = measure.offsetHeight;
  container.removeChild(measure);

  if (charW === 0 || charH === 0) return { cols: 80, rows: 24 };
  const cols = Math.floor(container.clientWidth / charW);
  const rows = Math.floor(container.clientHeight / charH);
  return { cols: Math.max(cols, 20), rows: Math.max(rows, 4) };
}

/** Render one line's styled spans; blank lines keep their height via nbsp. */
function renderSpans(spans: StyledSpan[]) {
  if (spans.length === 0) return <span>&nbsp;</span>;
  return spans.map((span, j) => (
    <span
      key={j}
      style={{
        color: span.inverse ? (span.bg ?? undefined) : (span.fg ?? undefined),
        backgroundColor: span.inverse
          ? (span.fg ?? undefined)
          : (span.bg ?? undefined),
        fontWeight: span.bold ? 700 : undefined,
        textDecoration: span.underline ? "underline" : undefined,
      }}
    >
      {span.text}
    </span>
  ));
}

export const Terminal = forwardRef<TerminalHandle, TerminalProps>(
  function Terminal({ onInput, onResize, connected, status }, ref) {
    // `lines` holds only completed (newline-terminated) lines. The trailing
    // partial line is rendered separately so that the next chunk can extend
    // it in place instead of appending a second copy of it.
    const [lines, setLines] = useState<TermLine[]>([]);
    const [partial, setPartial] = useState<StyledSpan[] | null>(null);
    const outputRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [, setHistory] = useState<string[]>([]);
    const [historyIdx, setHistoryIdx] = useState(-1);
    const [inputValue, setInputValue] = useState("");
    const autoScroll = useRef(true);
    const partialLine = useRef("");
    const dimsRef = useRef({ cols: 120, rows: 30 });

    const appendOutput = useCallback((raw: string) => {
      const text = partialLine.current + raw;
      const completed = text.split(/\r?\n/);
      partialLine.current = completed.pop() ?? "";

      if (completed.length > 0) {
        setLines((prev) => {
          const newLines = [...prev];
          for (const part of completed) {
            newLines.push({ spans: parseAnsi(part) });
          }
          if (newLines.length > MAX_SCROLLBACK) {
            return newLines.slice(newLines.length - MAX_SCROLLBACK);
          }
          return newLines;
        });
      }

      setPartial(
        partialLine.current ? parseAnsi(partialLine.current) : null,
      );
    }, []);

    const clear = useCallback(() => {
      setLines([]);
      setPartial(null);
      partialLine.current = "";
    }, []);

    useImperativeHandle(ref, () => ({ appendOutput, clear }), [
      appendOutput,
      clear,
    ]);

    // Auto-scroll to bottom
    useLayoutEffect(() => {
      if (autoScroll.current && outputRef.current) {
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
      }
    }, [lines, partial]);

    const handleScroll = useCallback(() => {
      if (!outputRef.current) return;
      const el = outputRef.current;
      autoScroll.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 20;
    }, []);

    // Measure terminal size on mount and resize
    useLayoutEffect(() => {
      if (!containerRef.current || !onResize) return;
      const el = containerRef.current;

      const measure = () => {
        const dims = measureCells(el);
        if (
          dims.cols !== dimsRef.current.cols ||
          dims.rows !== dimsRef.current.rows
        ) {
          dimsRef.current = dims;
          onResize(dims.cols, dims.rows);
        }
      };

      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }, [onResize]);

    const handleContainerClick = useCallback(() => {
      inputRef.current?.focus();
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          const cmd = inputValue;
          onInput(cmd + "\r\n");
          if (cmd.trim()) {
            setHistory((prev) => [...prev, cmd]);
          }
          setHistoryIdx(-1);
          setInputValue("");
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHistory((prev) => {
            const idx =
              historyIdx === -1 ? prev.length - 1 : historyIdx - 1;
            if (idx >= 0 && idx < prev.length) {
              setHistoryIdx(idx);
              setInputValue(prev[idx]);
            }
            return prev;
          });
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setHistory((prev) => {
            if (historyIdx === -1) return prev;
            const idx = historyIdx + 1;
            if (idx < prev.length) {
              setHistoryIdx(idx);
              setInputValue(prev[idx]);
            } else {
              setHistoryIdx(-1);
              setInputValue("");
            }
            return prev;
          });
        } else if (e.key === "c" && e.ctrlKey) {
          onInput("\x03");
        } else if (e.key === "l" && e.ctrlKey) {
          e.preventDefault();
          clear();
        }
      },
      [inputValue, historyIdx, onInput, clear],
    );

    return (
      <div
        className="gc-terminal"
        ref={containerRef}
        onClick={handleContainerClick}
      >
        <div
          className="gc-terminal__output"
          ref={outputRef}
          onScroll={handleScroll}
        >
          {lines.map((line, i) => (
            <div key={i} className="gc-terminal__line">
              {renderSpans(line.spans)}
            </div>
          ))}
          {partial && (
            <div className="gc-terminal__line">{renderSpans(partial)}</div>
          )}
        </div>
        <div className="gc-terminal__input-bar">
          <span className="gc-terminal__prompt">
            {connected ? "\u276f" : "\u00d7"}
          </span>
          <input
            ref={inputRef}
            className="gc-terminal__input"
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setHistoryIdx(-1);
            }}
            onKeyDown={handleKeyDown}
            disabled={!connected}
            placeholder={connected ? "Type a command..." : "Disconnected"}
            autoComplete="off"
            spellCheck={false}
          />
          {status && <span className="gc-terminal__status">{status}</span>}
        </div>
      </div>
    );
  },
);
